import { Button } from "@components/common/button";
import { DictType } from "@pma/types";
import axios from "axios";
import { parseCookies } from "nookies";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@hooks/use-toast";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import {
  SERVER_URL,
  PIN_TASK_ENDPOINT,
  PIN_PROJECT_ENDPOINT,
} from "@pma/utils/api-routes";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/common/tooltip";
import useTranslator from "@hooks/use-translator";
import { HTMLAttributes, useContext } from "react";
import { useAuth } from "@pma/stores/auth-store";
import { Pin, PinOff } from "lucide-react";
import { cn } from "@pma/utils/helpers";
import { ProjectContext } from "@pma/contexts/ProjectContext";

interface Props extends HTMLAttributes<HTMLDivElement> {
  project: DictType;
}

const PinProject = ({ project, children }: Props) => {
  const { translate } = useTranslator();
  const accessToken = parseCookies()[ACCESS_TOKEN];
  const { pinnedProjectResult, projectResult } = useContext(ProjectContext);

  const pinnedByUser = pinnedProjectResult?.data?.items.some(
    (p: DictType) => p?.id === project?.id,
  );

  const pinProject = async () => {
    const response = await axios.post(
      SERVER_URL + PIN_PROJECT_ENDPOINT + `/${project?.id}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const pinProjectMutation = useMutation({
    mutationKey: ["pin-project", project?.id],
    mutationFn: pinProject,
    onSuccess: (data) => {
      pinnedProjectResult?.refetch();
      projectResult?.refetch();

      toast({
        title: translate("Success"),
        description: `${project?.title} ${translate(pinnedByUser ? "has been unpinned" : "has been pinned")}`,
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: `${translate("Unable to pin")} ${project?.title}`,
        variant: "error",
      });
    },
  });

  return (
    <Tooltip>
      <TooltipTrigger
        onClick={(e) => {
          e.stopPropagation();
          pinProjectMutation.mutate();
        }}
      >
        {children ? (
          children
        ) : (
          <Button
            variant={"ghost"}
            size={"icon"}
            className="flex items-center cursor-pointer rounded-full justify-between w-6 h-6  text-xs m-0 p-1"
            onClick={(e)=>e.stopPropagation()}
          >
            {pinnedByUser ? <PinOff size={15} /> : <Pin size={15} />}
          </Button>
        )}
      </TooltipTrigger>
      <TooltipContent
        className={cn(
          "flex items-center gap-1 z-20",
          pinnedByUser ? "bg-destructive" : "bg-primary",
        )}
      >
        <h4>{translate(pinnedByUser ? "Unpin" : "Pin")}</h4>
      </TooltipContent>
    </Tooltip>
  );
};

export default PinProject;
